import { useEffect, type ReactNode } from 'react';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Right-edge sliding drawer for contextual help. Used by both the agency
 * and portal shells. ESC closes; clicking outside the panel closes.
 */
export function HelpDrawer({ open, onClose, title, children }: HelpDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-scope-navy/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex h-full w-full max-w-md flex-col overflow-hidden bg-white shadow-scope-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-scope-border-soft px-5 py-4">
          <h2 className="text-base font-semibold text-scope-navy">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close help"
            className="rounded-md p-1.5 text-scope-text-muted hover:bg-scope-cream-warm hover:text-scope-navy"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5 text-sm text-scope-text-muted">
          {children}
        </div>
      </div>
    </div>
  );
}
